<template>
  <div class="flex flex-col w-full">
    <div class="input-wrapper">
      <input
        :id="id"
        v-model="phoneNumber"
        class="input text-sm border-2
          appearance-none flex-auto
          min-w-0 w-full h-12 px-3
          focus focus:outline-none
          active:outline-none"
        :class="{
        'filled' : modelValue
        }"
        :style="{
        '--focus-border-color': !error ? global.primaryColor : '#D20000',
        '--box-color': boxColor,
        'border-color': error ? '#D20000' : defaultBorderColor,
        color: boxColor
        }"
        :type="type"
        :name="name"
        :autofocus="autofocus"
        :autocomplete="autocomplete"
        :disabled="$attrs.disabled"
        :maxlength="maxLength"
        :aria-label="$attrs['aria-label']"
        :aria-labelledby="$attrs['aria-labelledby']"
        :aria-invalid="error ? 'true' : $attrs['aria-invalid']"
        :aria-describedby="inputDescribedBy"
        :aria-required="$attrs['aria-required']"
        :required="hasRequiredAttribute"
        :inputmode="inputMode"
        @focus="$emit('focus', $event)"
        @blur="$emit('blur', $event)"
        @input="sanitizePhoneNumber"
        @keyup="$emit('get-phone-number', getPhoneNumberAndCountryCode())"
      >
      <span
        v-if="valid && !error"
        class="validation-icon-outside"
        aria-hidden="true"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path :stroke="successColor" d="M3 8.5L6.5 12L13 4" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </span>
    </div>
    <p
      v-if="error && visibleErrorMessage"
      :id="`${id}-error`"
      class="input-error-message"
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
    >
      {{ visibleErrorMessage }}
    </p>
    <div
      :id="statusMessageId"
      class="sr-only"
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      {{ statusAnnouncement }}
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { mapGetters } from 'vuex';
import { hexToRgba } from '@/utils';
// eslint-disable-next-line
const intlTelInput = require('intl-tel-input');

declare global {
  interface Window {
    intlTelInputGlobals: any;
  }
}

export default defineComponent({
  name: 'Input',
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    id: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: ''
    },
    autofocus: {
      type: Boolean,
      default: false
    },
    autocomplete: {
      type: String,
      default: ''
    },
    name: {
      type: String,
      default: ''
    },
    type: {
      type: String,
      default: '',
      required: true
    },
    error: {
      type: Boolean,
      default: false
    },
    errorMessage: {
      type: String,
      default: ''
    },
    valid: {
      type: Boolean,
      default: false
    },
    maxLength: {
      type: Number,
      default: 12
    },
    inputMode: {
      type: String,
      default: 'tel'
    }
  },
  emits: ['get-phone-number', 'focus', 'blur'],
  computed: {
    ...mapGetters([
      'global'
    ]),
    hasRequiredAttribute (): boolean {
      return this.$attrs.required !== undefined ||
        this.$attrs['aria-required'] === true ||
        this.$attrs['aria-required'] === 'true';
    },
    boxColor (): string {
      return this.global.boxColor || '#696E79';
    },
    successColor (): string {
      return this.global.successColor || '#337E33';
    },
    hasValue (): boolean {
      return !!this.phoneNumber;
    },
    defaultBorderColor (): string {
      return this.hasValue
        ? this.boxColor
        : hexToRgba(this.boxColor, 0.5);
    },
    inputDescribedBy (): string | undefined {
      const ids: string[] = [];
      const describedBy = this.$attrs['aria-describedby'];

      if (typeof describedBy === 'string' && describedBy.trim()) {
        ids.push(describedBy.trim());
      }

      if (this.statusAnnouncement && this.statusMessageId) {
        ids.push(this.statusMessageId);
      }

      if (this.error && this.errorMessage && this.id) {
        ids.push(`${this.id}-error`);
      }

      return ids.length ? ids.join(' ') : undefined;
    },
    fieldLabelText (): string {
      const ariaLabel = this.$attrs['aria-label'];

      if (typeof ariaLabel === 'string' && ariaLabel.trim()) {
        return ariaLabel.trim();
      }

      return this.placeholder.replace(/\*/g, '').trim();
    },
    statusAnnouncement (): string {
      if (!this.valid || !this.phoneNumber || this.error || !this.fieldLabelText) {
        return '';
      }

      return this.$t('message.accessibility.field_valid', {
        field: this.fieldLabelText
      }) as string;
    },
    statusMessageId (): string | undefined {
      return this.id ? `${this.id}-status` : undefined;
    },
    visibleErrorMessage (): string {
      if (!this.errorMessage) {
        return '';
      }

      return this.errorMessage.startsWith('* ')
        ? this.errorMessage
        : `* ${this.errorMessage}`;
    }
  },
  data() {
    return {
      phoneNumber: ''
    };
  },

  mounted () {
    const input = document.getElementById('tel');
    intlTelInput(input, {
      initialCountry: 'it',
      preferredCountries: ['it'],
      // onlyCountries: ['it', 'fr', 'de', 'gb']
    });
    if(input){
      input.addEventListener('countrychange', () => {
        this.$emit('get-phone-number', this.getPhoneNumberAndCountryCode());
      });
    }
  },
  methods: {
    onLabelClick (label: any) {
      label.srcElement.previousElementSibling.focus();
    },
    getPhoneNumberAndCountryCode() {
      let dialCode = '';
      const input = document.getElementById('tel');
      if (input != null) {
        const iti = window.intlTelInputGlobals.getInstance(input);
        dialCode = iti.getSelectedCountryData().dialCode.toString();    

        return {
          countryCode: `+${dialCode}`,
          number: this.phoneNumber
        };
      }
    },
    sanitizePhoneNumber(event: any) {
      this.phoneNumber = event.target.value.replace(/\D/g, '');
    }
  }
});
</script>
<style>
.input:focus + .label,
.input:active + .label,
.input.filled + .label {
  font-size: 0.65rem;
  transition: all 0.2s ease-out;
  top: -0.1rem;
  color: #7b808c;
}

.input {
  border-radius: 5px;
}

.input:focus {
  border-color: var(--focus-border-color) !important;
}

.label {
  transition: all 0.2s ease-out;
  top: 0.6rem;
  left: 0;
}

.label-disabled {
  font-size: 0.65rem;
  top: 0.1rem;
  left: 0;
}

.input-error-message {
  margin: 0.25rem 0 0 0;
  font-size: 0.75rem;
  color: #D20000;
  font-weight: 700;
  line-height: 1.25;
  text-align: left;
}

.input-wrapper {
  position: relative;
  width: 100%;
}

.input-wrapper .iti {
  display: block;
  width: 100%;
}

.input-wrapper .iti input {
  width: 100%;
}

.validation-icon-outside {
  position: absolute;
  right: -1.5rem;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}
</style>
